import { useBundle } from '@amzn/react-arb-tools';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Card } from '@amzn/stencil-react-components/card';
import { H1, H2, H3, H4, Text } from '@amzn/stencil-react-components/text';
import {
  Col,
  Flex,
  GridContainer,
  GridItem,
  Row,
} from '@amzn/stencil-react-components/layout';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from '../constants';
import { t } from '../helpers';
import SignUpSection from './SignUpSection';
import { PropsWithChildren, ReactElement, useState, ReactNode } from 'react';
import {
  Button,
  ButtonIconPosition,
  ButtonVariant,
} from '@amzn/stencil-react-components/button';
import { Chip, ChipSize } from '@amzn/stencil-react-components/chip';
import { IconArrowRight } from '@amzn/stencil-react-icons';
import eventsCardImage1 from '../images/eventsCardImage1.svg';
import eventsCardImage2 from '../images/eventsCardImage2.svg';
import eventsCardImage3 from '../images/eventsCardImage3.svg';
import thriveImage from '../images/thriveImage.png';
import spotlightImage from '../images/spotlightImage.png';
import howItWorksImage from '../images/howItWorksImage.png';
import { useBreakpoints } from '@amzn/stencil-react-components/responsive';

const PADDING_300LR_400TB = {
  left: 'dimensions.spacing.300',
  right: 'dimensions.spacing.300',
  top: 'dimensions.spacing.400',
  bottom: 'dimensions.spacing.400',
};

const PADDING_200LR = {
  left: 'dimensions.spacing.200',
  right: 'dimensions.spacing.200',
};

const PAGE_WIDTH = 1440;

const PageSectionCol = ({
  id,
  background,
  children,
  center,
}: PropsWithChildren<{
  id: string;
  background: string;
  center?: boolean | undefined;
}>) => (
  <Col
    id={id}
    alignItems="center"
    padding={PADDING_300LR_400TB}
    backgroundColor={background}
  >
    <Col
      maxWidth={PAGE_WIDTH}
      gridGap="dimensions.spacing.400"
      width={'100%'}
      padding={PADDING_200LR}
      margin={{ left: 'auto', right: 'auto' }}
      alignItems={center ? 'center' : undefined}
    >
      {children}
    </Col>
  </Col>
);

const PageSection = ({
  id,
  pillar,
  background,
  bundle,
  children,
  center,
}: PropsWithChildren<{
  id: string;
  background: string;
  bundle: any;
  center?: boolean | undefined;
  pillar?: boolean | undefined;
}>) => (
  <PageSectionCol id={id} background={background} center={center}>
    <>
      {pillar ? (
        <Text
          color="color.text.accent-on-bg-default"
          style={{
            fontSize: '1.125rem',
            fontWeight: '700',
            lineHeight: '1.688',
          }}
        >
          {t(bundle, `${id}_title`)}
        </Text>
      ) : null}
      <H2
        style={{
          fontSize: '2rem',
          fontWeight: '800',
          lineHeight: '1.1',
        }}
      >
        {t(bundle, `${id}_header`)}
      </H2>
      <Text>{t(bundle, `${id}_description`)}</Text>
      {children}
    </>
  </PageSectionCol>
);

const InformationCardsGroup = ({
  cards,
  showMore,
  showMoreAction,
  bundle,
}: {
  cards: ReactElement[];
  showMore?: boolean | undefined;
  showMoreAction?: () => void | undefined;
  bundle: any;
}) => (
  <Col width="100%" gridGap={'dimensions.spacing.400'}>
    <GridContainer>
      {(showMore ? cards : cards.slice(0, 3)).map((card, index) => {
        return (
          <GridItem lg={4} md={4} sm={4} key={index}>
            {card}
          </GridItem>
        );
      })}
    </GridContainer>
    {showMoreAction && (
      <Row>
        <Button
          variant={ButtonVariant.Tertiary}
          iconPosition={ButtonIconPosition.Trailing}
          icon={<IconArrowRight />}
          onClick={showMoreAction}
        >
          {showMore ? t(bundle, 'show_less') : t(bundle, 'show_more')}
        </Button>
      </Row>
    )}
  </Col>
);

const InformationCard = ({
  title,
  content,
  buttonText,
  buttonIcon,
  buttonAction,
  bundle,
  chip,
  image,
  imageAlt,
  backgroundColor,
}: {
  title: string;
  content: string;
  buttonText: string;
  buttonAction: () => void;
  bundle: any;
  buttonIcon?: ReactNode;
  backgroundColor?: string | undefined;
  chip?: string | undefined;
  image?: string | undefined;
  imageAlt?: string | undefined;
}) => (
  <Card
    width="100%"
    height="100%"
    padding={0}
    overflow="hidden"
    backgroundColor={backgroundColor}
  >
    <Col width="100%" height="100%">
      {image && imageAlt && (
        <Flex flexDirection="row" alignItems="center" justifyContent="center">
          <img
            style={{
              width: '100%',
            }}
            src={image}
            alt={t(bundle, imageAlt)}
          />
        </Flex>
      )}
      <Col
        gridGap="dimensions.spacing.200"
        padding="dimensions.spacing.300"
        width={'100%'}
        flexGrow={1}
      >
        <H3>{t(bundle, title)}</H3>
        {chip && <Chip size={ChipSize.ExtraSmall}>{t(bundle, chip)}</Chip>}
        <Col
          gridGap="dimensions.spacing.400"
          flexGrow={1}
          justifyContent="space-between"
        >
          <Text>{t(bundle, content)}</Text>
          <Row>
            <Button
              iconPosition={ButtonIconPosition.Trailing}
              icon={buttonIcon}
              onClick={buttonAction}
            >
              {t(bundle, buttonText)}
            </Button>
          </Row>
        </Col>
      </Col>
    </Col>
  </Card>
);

const SuccessHighlight = ({ bundle }: { bundle: any }) => (
  <>
    {t(bundle, 'success_header_prefix')}{' '}
    <span style={{ color: token('color.text.accent-on-bg-default') }}>
      {t(bundle, 'success_header_highlight')}
    </span>
  </>
);

const CardSuccessMessage = ({ bundle }: { bundle: any }) => (
  <Col
    gridGap="dimensions.spacing.200"
    width="100%"
    padding="dimensions.spacing.400"
    dataTestId="sign-up-success-header-test-id"
    role="status"
    aria-live="polite"
  >
    <H4 fontSize={'1.375rem'} lineHeight={1.15}>
      <SuccessHighlight bundle={bundle} />
    </H4>
    <Text fontWeight="700">{t(bundle, 'success_subheader')}</Text>
    <Text>{t(bundle, 'success_description')}</Text>
    <Text>{t(bundle, 'success_description_page')}</Text>
    <Text fontWeight="700">{t(bundle, 'success_what_to_expect_header')}</Text>
    <Text>{t(bundle, 'success_what_to_expect_description')}</Text>
    <Text>{t(bundle, 'success_welcome_closing')}</Text>
  </Col>
);

const FooterSuccessMessage = ({ bundle }: { bundle: any }) => (
  <PageSectionCol
    id={'submit_interest_footer'}
    background={'color.surface.bg-default'}
    center={true}
  >
    <Col
      alignItems="center"
      gridGap="dimensions.spacing.200"
      width="100%"
      dataTestId="sign-up-success-footer-test-id"
      role="status"
      aria-live="polite"
    >
      <H2
        style={{
          fontSize: '2rem',
          fontWeight: '800',
          lineHeight: '1.1',
          textAlign: 'center',
        }}
      >
        <SuccessHighlight bundle={bundle} />
      </H2>
      <Text fontWeight="700" textAlign="center">
        {t(bundle, 'success_subheader')}
      </Text>
      <Text textAlign="center">{t(bundle, 'success_description')}</Text>
      <Button
        variant={ButtonVariant.Primary}
        onClick={() =>
          window.open('https://www.aboutamazon.com/about-us', '_blank')
        }
      >
        {t(bundle, 'success_explore_button')}
      </Button>
    </Col>
  </PageSectionCol>
);

const GrowThriveConnectPage = () => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const [expandedGrow, setExpandedGrow] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  const { matches } = useBreakpoints();

  const PILLARS = [
    {
      id: 'grow',
      content: (
        <InformationCardsGroup
          cards={[
            <InformationCard
              title="grow_aws_skill_builder"
              chip="grow_aws_skill_builder_chip"
              content="grow_aws_skill_builder_content"
              buttonText="grow_aws_skill_builder_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="grow_aws_skill_center"
              chip="grow_aws_skill_center_chip"
              content="grow_aws_skill_center_content"
              buttonText="grow_aws_skill_center_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="grow_aws_skill_restart"
              chip="grow_aws_skill_restart_chip"
              content="grow_aws_skill_restart_content"
              buttonText="grow_aws_skill_restart_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="grow_aws_skill_kiro"
              chip="grow_aws_skill_kiro_chip"
              content="grow_aws_skill_kiro_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="grow_aws_skill_q"
              chip="grow_aws_skill_q_chip"
              content="grow_aws_skill_q_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="grow_aws_skill_partyrock"
              chip="grow_aws_skill_partyrock_chip"
              content="grow_aws_skill_partyrock_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
          ]}
          showMore={expandedGrow}
          showMoreAction={() => setExpandedGrow(!expandedGrow)}
          bundle={bundle}
        />
      ),
      background: 'color.surface.bg-default',
    },
    {
      id: 'thrive',
      content: (
        <>
          <img
            src={thriveImage}
            style={{
              width: '100%',
              height: '400px',
              objectFit: 'cover',
              borderRadius: '15px',
            }}
            alt={t(bundle, 'thrive_image_alt')}
          />
          <InformationCardsGroup
            cards={[
              <InformationCard
                title="thrive_amazon_access"
                content="thrive_amazon_access_content"
                buttonText="learn_more_button"
                buttonIcon={<IconArrowRight />}
                bundle={bundle}
                buttonAction={() =>
                  window.open('https://www.aboutamazon.com/about-us', '_blank')
                }
              />,
              <InformationCard
                title="thrive_amazon_pharmacy"
                content="thrive_amazon_pharmacy_content"
                buttonText="learn_more_button"
                buttonIcon={<IconArrowRight />}
                bundle={bundle}
                buttonAction={() =>
                  window.open('https://www.aboutamazon.com/about-us', '_blank')
                }
              />,
              <InformationCard
                title="thrive_amazon_one_medical"
                content="thrive_amazon_one_medical_content"
                buttonText="learn_more_button"
                buttonIcon={<IconArrowRight />}
                bundle={bundle}
                buttonAction={() =>
                  window.open('https://www.aboutamazon.com/about-us', '_blank')
                }
              />,
            ]}
            bundle={bundle}
          />
        </>
      ),
      background: 'color.surface.bg-muted',
    },
    {
      id: 'connect',
      content: (
        <InformationCardsGroup
          cards={[
            <InformationCard
              title="connect_amazon_supplier_connect"
              content="connect_amazon_supplier_connect_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="connect_amazon_sidewalk"
              content="connect_amazon_sidewalk_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
            <InformationCard
              title="connect_amazon_military_spouse_program"
              content="connect_amazon_military_spouse_program_content"
              buttonText="learn_more_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
            />,
          ]}
          bundle={bundle}
        />
      ),
      background: 'color.surface.bg-default',
    },
  ];

  function titleSection(): ReactNode {
    return (
      <PageSectionCol
        id={'grow-thrive-connect-header'}
        background="color.surface.bg-muted"
      >
        <GridContainer>
          <GridItem lg={6} md={8}>
            <Col gridGap="dimensions.spacing.300" justifyContent="center">
              <H1 id="gtc-header" lineHeight={1} fontSize={'5.875rem'}>
                {t(bundle, 'page_title_grow')}{' '}
                <span
                  style={{ color: token('color.text.accent-on-bg-default') }}
                >
                  {t(bundle, 'page_title_thrive')}{' '}
                </span>
                {t(bundle, 'page_title_connect')}
              </H1>
              <H2 id="pathway-subheader">{t(bundle, 'page_sub_title')}</H2>
              <Text>{t(bundle, 'page_title_description')}</Text>
              <Row>
                <Button>{t(bundle, 'explore_button')}</Button>
              </Row>
            </Col>
          </GridItem>
          <GridItem lg={6} md={8}>
            <Col gridGap="dimensions.spacing.100" width={'100%'}>
              <Card width={'100%'}>
                {submitSuccess ? (
                  <CardSuccessMessage bundle={bundle} />
                ) : (
                  <Col
                    gridGap="dimensions.spacing.100"
                    width={'100%'}
                    padding="dimensions.spacing.400"
                  >
                    <H4 fontSize={'1.375rem'} lineHeight={1.15}>
                      {t(bundle, 'sign_up_to_join_header')}
                    </H4>
                    <Text>{t(bundle, 'submit_interest_description')}</Text>
                    <SignUpSection
                      footer={false}
                      onSuccess={() => setSubmitSuccess(true)}
                    />
                    <Text>{t(bundle, 'submit_interest_disclaimer')}</Text>
                  </Col>
                )}
              </Card>
            </Col>
          </GridItem>
        </GridContainer>
      </PageSectionCol>
    );
  }

  return (
    <Col dataTestId="grow-thrive-connect-test-id">
      {titleSection()}

      <PageSection
        id="how_it_works"
        background="color.surface.bg-default"
        bundle={bundle}
      >
        <img
          src={howItWorksImage}
          style={{
            width: '100%',
            height: '480px',
            objectFit: 'cover',
            borderRadius: '15px',
          }}
          alt={t(bundle, 'how_it_works_image_alt')}
        />
        <InformationCardsGroup
          cards={[
            <InformationCard
              title="how_it_works_grow"
              content="how_it_works_grow_content"
              buttonText="learn_more_button"
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              backgroundColor="color.surface.bg-muted"
            />,
            <InformationCard
              title="how_it_works_thrive"
              content="how_it_works_thrive_content"
              buttonText="learn_more_button"
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              backgroundColor="color.surface.bg-muted"
            />,
            <InformationCard
              title="how_it_works_connect"
              content="how_it_works_connect_content"
              buttonText="learn_more_button"
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              backgroundColor="color.surface.bg-muted"
            />,
          ]}
          bundle={bundle}
        />
      </PageSection>

      <PageSectionCol id="community-spotlight" background="#232f3e">
        <GridContainer>
          <GridItem lg={8} md={4} sm={4}>
            <Col gridGap={'dimensions.spacing.100'}>
              <Text
                color="color.text.accent-on-bg-default"
                style={{
                  fontSize: '0.875rem',
                  fontWeight: '700',
                }}
              >
                {t(bundle, 'community_spotlight_title')}
              </Text>
              <Text
                fontWeight="700"
                fontSize="2rem"
                lineHeight="1.25"
                color="#ffffff"
              >
                {t(bundle, 'community_spotlight_quote')}
              </Text>
              <Text color="#ffffff">
                {t(bundle, 'community_spotlight_quote_attribution')}
              </Text>
            </Col>
          </GridItem>
          <GridItem lg={4} md={4} sm={4}>
            <Row
              justifyContent={matches.s ? 'center' : 'flex-end'}
              width={'100%'}
            >
              <img
                src={spotlightImage}
                style={{
                  width: '374px',
                  height: '290px',
                }}
                alt={t(bundle, 'spotlight_image_alt')}
              />
            </Row>
          </GridItem>
        </GridContainer>
      </PageSectionCol>

      {PILLARS.map((pillar) => {
        return (
          <PageSection
            key={pillar.id}
            pillar={true}
            id={pillar.id}
            background={pillar.background}
            bundle={bundle}
          >
            {pillar.content}
          </PageSection>
        );
      })}

      <PageSection
        id={'upcoming_events'}
        background={'color.surface.bg-default'}
        bundle={bundle}
      >
        <InformationCardsGroup
          cards={[
            <InformationCard
              title="upcoming_events_ai_fundamentals"
              chip="upcoming_events_ai_fundamentals_chip"
              content="upcoming_events_ai_fundamentals_content"
              buttonText="register_now_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              image={eventsCardImage1}
              imageAlt="upcoming_events_image1_alt"
            />,
            <InformationCard
              title="upcoming_events_ai_fundamentals_spanish"
              chip="upcoming_events_ai_fundamentals_spanish_chip"
              content="upcoming_events_ai_fundamentals_spanish_content"
              buttonText="register_now_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              image={eventsCardImage2}
              imageAlt="upcoming_events_image2_alt"
            />,
            <InformationCard
              title="upcoming_events_aws_skill_center"
              chip="upcoming_events_aws_skill_center_chip"
              content="upcoming_events_aws_skill_center_content"
              buttonText="register_now_button"
              buttonIcon={<IconArrowRight />}
              bundle={bundle}
              buttonAction={() =>
                window.open('https://www.aboutamazon.com/about-us', '_blank')
              }
              image={eventsCardImage3}
              imageAlt="upcoming_events_image3_alt"
            />,
          ]}
          bundle={bundle}
        />
      </PageSection>

      {submitSuccess ? (
        <FooterSuccessMessage bundle={bundle} />
      ) : (
        <PageSection
          id={'submit_interest_footer'}
          background={'color.surface.bg-default'}
          bundle={bundle}
          center={true}
        >
          <Row
            justifyContent="center"
            gridGap="dimensions.spacing.100"
            width={'100%'}
          >
            <SignUpSection
              footer={true}
              onSuccess={() => setSubmitSuccess(true)}
            />
          </Row>
        </PageSection>
      )}
    </Col>
  );
};

export default GrowThriveConnectPage;
